import type { OrderbookSide, AnimatedPriceLevel } from '@exchange/libs/orderbook/service/src';

import type { DepthChartPriceLevel } from './interfaces';

export const MIN_STARTING_ZOOM = 2;
export const MIN_POSSIBLE_ZOOM = 0.1;
export const MAX_POSSIBLE_ZOOM = 99;

export const FPS_BASE = 5;
export const FPS_DURING_HOVER = 30;

export const MIN_DCH_PL_NUMBER = 3;

export const clamp = (min: number, mid: number, max: number) => Math.min(Math.max(min, mid), max);

export const getPricelevelsWithSum = (pricelevels: Array<AnimatedPriceLevel>, orderbookSide: OrderbookSide) => {
  const returned: Array<DepthChartPriceLevel> = [];

  for (let i = 0; i < pricelevels.length; i += 1) {
    const priceLevel = pricelevels[i];

    if (priceLevel) {
      returned.push({
        side: priceLevel.side,
        price: priceLevel.price,
        amount: priceLevel.amount,
        total: priceLevel.total,
        sum: priceLevel.amount + (i === 0 ? 0 : (returned[i - 1]?.sum ?? 0)),
      });
    }
  }

  while (returned.length < MIN_DCH_PL_NUMBER) {
    returned.push({
      side: orderbookSide,
      price: 0,
      amount: 0,
      total: 0,
      sum: 0,
    });
  }

  return returned;
};

const calculatePriceDifference = (newNum: number, originalNum: number): number => Math.abs(((newNum - originalNum) / originalNum) * 100);

export const getMinZoomPercent = (midPrice: number, askDataPrice: number, bidDataPrice: number): number => {
  if (!midPrice) {
    return MIN_POSSIBLE_ZOOM;
  }

  const increaseAsk = calculatePriceDifference(askDataPrice, midPrice);
  const decreaseBid = calculatePriceDifference(bidDataPrice, midPrice);
  const percent = clamp(MIN_POSSIBLE_ZOOM, Math.max(Math.ceil(increaseAsk), Math.ceil(decreaseBid)), MAX_POSSIBLE_ZOOM);

  return percent ? Number(percent.toFixed(2)) : MIN_POSSIBLE_ZOOM;
};

export const getStartingZoomPercent = (midPrice: number, askDataPrice?: number, bidDataPrice?: number): number => {
  let decreaseBid = MIN_STARTING_ZOOM;
  let increaseAsk = MIN_STARTING_ZOOM;

  if (!midPrice) {
    return MIN_STARTING_ZOOM;
  }

  if (askDataPrice && bidDataPrice) {
    decreaseBid = calculatePriceDifference(bidDataPrice, midPrice) + (MIN_STARTING_ZOOM - 1);
    increaseAsk = calculatePriceDifference(askDataPrice, midPrice) + (MIN_STARTING_ZOOM - 1);
  }

  const percent = clamp(MIN_POSSIBLE_ZOOM, Math.max(Math.ceil(increaseAsk), Math.ceil(decreaseBid)), MAX_POSSIBLE_ZOOM);

  return percent ? Number(percent.toFixed(2)) : MIN_STARTING_ZOOM;
};

export const calcMidPrice = (firstAsk: number, firstBid: number): number => {
  if (firstAsk !== 0 && firstBid !== 0) {
    return (firstAsk + firstBid) / 2;
  }

  if (firstBid === 0 && firstAsk === 0) {
    return 0;
  }

  if (firstBid !== 0) {
    return firstBid;
  }

  return firstAsk;
};
